import { useState, useCallback, useRef, useEffect } from "react";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import { getVagaAssistantSessionMessages } from "@/services/agents-api";
import { apiFetch } from "@/lib/api-fetch";
import { ApiError, formatApiError } from "@/lib/api-error";
import { reportApiError } from "@/lib/report-error";
import { trackEvent, type EventResult } from "@/lib/telemetry";

const AGENTS_BASE_URL = `${SUPABASE_URL}agents`;

/**
 * Status da mensagem do assistente. Importante pra UX nunca ficar "em branco":
 * - `streaming`: chunks chegando em tempo real.
 * - `partial`: stream cortado no meio (provavelmente queda de rede ou rollout de
 *   deploy ECS). Mantém o conteúdo recebido até ali com nota visual.
 * - `retrying`: tentando reconectar após corte; mostra tentativa N/M.
 * - `error`: esgotou retries ou erro não-retryable (ex.: 4xx). Botão "Tentar
 *   novamente" disponível.
 * - `complete`: stream chegou ao fim com `event: done`.
 *
 * Sem status (`undefined`) = legado / role !== "assistant" — renderiza normal.
 */
export type AssistantMessageStatus =
  | "streaming"
  | "partial"
  | "retrying"
  | "error"
  | "complete";

export interface TransparenciaFiltro {
  filtro: string;
  efeito: string;
}

/** Metadados de paginação/comparação das listas de cards (vem do backend). */
export interface CardsMeta {
  total_primary: number;
  total_exploratory: number;
  ids_primary: string[];
  ids_exploratory: string[];
  novos_learned_count: number;
  page_size: number;
}

export interface AssistantMessage {
  id: string;
  role: "user" | "assistant" | "tool";
  content: string;
  toolName?: string;
  toolArgs?: Record<string, unknown>;
  freelancersMencionados?: string[];
  timestamp: Date;
  status?: AssistantMessageStatus;
  retryAttempt?: number;
  retryMax?: number;
  retryDelayMs?: number;
  errorDetail?: string;
  /** Cards ricos de freelancers renderizados inline no fluxo da conversa (página 1). */
  cards?: FreelancerSugerido[];
  /** Lista paralela "aprendida" (busca_match dual) — renderizada lado a lado (página 1). */
  cardsExploratory?: FreelancerSugerido[];
  /** Resumo de como cada filtro soft afetou o ranking (busca_match). */
  transparencia?: TransparenciaFiltro[];
  /** Metadados pra paginar/comparar as listas sem baixar o conjunto inteiro. */
  cardsMeta?: CardsMeta;
  /** ID da mensagem de cards no servidor — usado pra buscar páginas seguintes. */
  serverId?: string;
  /** Em conversas reabertas a lista nasce recolhida (clicar no número expande). */
  cardsCollapsedDefault?: boolean;
}

export interface FreelancerSugerido {
  id: string;
  nome: string;
  areas?: string[];
  cidade?: string;
  pretensao_salarial?: string;
  portfolio_url?: string;
  linkedin_url?: string;
  media_perfil?: number;
  media_projeto?: number;
  portfolio_score?: number;
  score_composto?: number;
  combined_score?: number;
  /** Score de match da vaga (motor de recomendação) — quando vem do busca_match. */
  overall_score?: number;
  /** Breakdown por dimensão (category_score, value_match_score, ...) — usado no diff dual. */
  match_breakdown?: Record<string, number>;
  similarity?: number;
  style_tags?: string[];
  experiencia?: string;
  /** Foto/avatar (profiles.avatar_url); pode ser null → card usa iniciais. */
  foto_url?: string | null;
  /** Pontos fortes / ressalvas prontos do motor de match (busca_match). */
  pontos_fortes?: string[];
  ressalvas?: string[];
  match_resumo?: string;
  /**
   * Marcado manualmente como indicado para a agência da vaga no Radar de
   * Perfis (curadoria humana, mesma flag do `match_explanation.radar_indicado`
   * do motor de recomendação — propagada aqui pelo busca_match).
   */
  radar_indicado?: boolean;
  /** Sinais soft que afetaram o ranking deste freelancer (ex.: "tag ✓"). */
  soft_signals?: string[];
  disponibilidade?: {
    status: string;
    texto: string;
    projetos_ativos: number;
    proxima_liberacao?: string;
  };
  status_disponibilidade?:
    | "disponivel"
    | "indisponivel"
    | "temporariamente_indisponivel"
    | "trabalhando_fixo"
    | "nao_avaliado"
    | null;
  notas_avaliacao?: Record<string, Record<string, number>>;
}

/**
 * Sugestão pós-busca como chip clicável. `label` é o texto do botão, `prompt` é
 * a mensagem enviada no clique (COR-277: assim o chip pode ser uma pergunta
 * legível sem que o front tenha que recortar a string pra achar o comando).
 */
export interface SugestaoAtiva {
  label: string;
  prompt: string;
}

interface UseVagaAssistantReturn {
  messages: AssistantMessage[];
  isLoading: boolean;
  sessionId: string | null;
  freelancersSugeridos: FreelancerSugerido[];
  suggestions: string[];
  sugestoesAtivas: SugestaoAtiva[];
  loadingSuggestions: boolean;
  loadingSession: boolean;
  activeVagaId: string | null;
  sendMessage: (message: string) => Promise<void>;
  retryLastMessage: () => Promise<void>;
  /**
   * Aborta o stream SSE atual (botão "Parar resposta"). Cancela retries
   * pendentes — abort manual não é tratado como erro recuperável.
   */
  cancelStream: () => void;
  clearMessages: () => void;
  loadSession: (sessionId: string, vagaId: string) => Promise<void>;
}

/**
 * Aceita o formato novo ({label, prompt}) e o antigo (string), porque uma sessão
 * já aberta pode receber payload da versão anterior da API durante o deploy.
 */
function normalizarSugestoes(raw: unknown[]): SugestaoAtiva[] {
  return raw.flatMap((item) => {
    if (typeof item === "string") {
      return [{ label: item, prompt: item.split(" — ")[0].replace(/^\d+ /, "") }];
    }
    if (item && typeof item === "object") {
      const { label, prompt } = item as Partial<SugestaoAtiva>;
      if (label) return [{ label, prompt: prompt || label }];
    }
    return [];
  });
}

const RETRY_DELAYS_MS = [300, 900, 2700]; // 3 tentativas, backoff exponencial

/**
 * Decide se vale retentar. Cobre:
 * - `TypeError: network error` / `TypeError: Failed to fetch` (deploy ECS corta
 *   conexões SSE longas → fetch rejeita; ver Sentry CORISCOFRILA-APP-1Q).
 * - `AbortError` por timeout do navegador / conexão lenta.
 * - HTTP 5xx (erro transiente do servidor).
 * 4xx fica fora — bug do cliente, retentar não muda nada.
 */
function isRetryableError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status >= 500 && err.status < 600;
  }
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (err.name === "TypeError" || err.name === "AbortError") return true;
    if (msg.includes("network") || msg.includes("failed to fetch")) return true;
    if (msg.includes("stream") && msg.includes("disponível")) return true; // stream cortado
  }
  return false;
}

export function useVagaAssistant(vagaId: string, enabled = false): UseVagaAssistantReturn {
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [freelancersSugeridos, setFreelancersSugeridos] = useState<FreelancerSugerido[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [activeVagaId, setActiveVagaId] = useState<string | null>(null);
  const [sugestoesAtivas, setSugestoesAtivas] = useState<SugestaoAtiva[]>([]);
  const [suggestionsRefresh, setSuggestionsRefresh] = useState(0);
  const messageIdCounter = useRef(0);

  const nextId = () => {
    messageIdCounter.current += 1;
    return `msg-${messageIdCounter.current}`;
  };

  // Carregar sugestões apenas quando o painel estiver aberto
  useEffect(() => {
    if (!vagaId || !enabled) return;
    let cancelled = false;

    const fetchSuggestions = async () => {
      setLoadingSuggestions(true);
      try {
        const data = await apiFetch<{ suggestions?: unknown }>(
          `${AGENTS_BASE_URL}/vaga-assistant/suggestions?vaga_id=${vagaId}`,
        );
        if (!cancelled && Array.isArray(data.suggestions)) {
          setSuggestions(data.suggestions);
        }
      } catch (error) {
        // Silencioso na UI (sugestões são opcionais), mas reporta 5xx/rede ao Sentry.
        reportApiError(error, { source: "vaga_assistant", kind: "suggestions" });
      } finally {
        if (!cancelled) setLoadingSuggestions(false);
      }
    };

    fetchSuggestions();
    return () => { cancelled = true; };
  }, [vagaId, enabled, suggestionsRefresh]);

  const loadSession = useCallback(async (targetSessionId: string, targetVagaId: string) => {
    setLoadingSession(true);
    try {
      const data = await getVagaAssistantSessionMessages(targetVagaId, targetSessionId);
      const loadedMessages: AssistantMessage[] = data.messages
        // Mantém mensagens de texto e também as só-de-cards (content vazio).
        .filter((m) => m.content || (m.freelancer_cards && m.freelancer_cards.length > 0))
        .map((m) => {
          const cards = m.freelancer_cards || undefined;
          const cardsExploratory = m.freelancer_cards_exploratory || undefined;
          return {
            id: nextId(),
            role: m.role as "user" | "assistant" | "tool",
            content: m.content || "",
            freelancersMencionados: m.freelancers_mencionados || undefined,
            cards,
            cardsExploratory,
            transparencia: m.transparencia || undefined,
            cardsMeta: m.cards_meta || undefined,
            serverId: cards ? m.id : undefined,
            // Reaberta = renderiza markdown estável (não streaming) e cards recolhidos.
            cardsCollapsedDefault: !!cards,
            status: "complete" as const,
            timestamp: new Date(m.created_at),
          };
        });

      setMessages(loadedMessages);
      setSessionId(targetSessionId);
      setActiveVagaId(targetVagaId);
      setFreelancersSugeridos([]);
    } catch (error) {
      console.error("Erro ao carregar sessão:", error);
    } finally {
      setLoadingSession(false);
    }
  }, []);

  // Guarda a última mensagem enviada pra suportar retry manual via UI.
  const lastUserMessageRef = useRef<string | null>(null);
  // Controla fetch atual pra suportar botão "Parar resposta" + flag pra
  // distinguir cancelamento manual (não retentar) de outras AbortErrors
  // (timeout do navegador → retentar).
  const abortControllerRef = useRef<AbortController | null>(null);
  const userCancelledRef = useRef(false);
  // What the running turn did, for analytics: never the text of the conversation.
  const turnUsageRef = useRef({ tools: [] as string[], freelancers: 0, serverError: false });
  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = sessionId;

  /**
   * Faz UMA tentativa de stream SSE. Pode ser chamada várias vezes pelo loop
   * de retry. Retorna o estado final da mensagem assistente (id + content)
   * pra que o caller decida o que fazer (mostrar erro, retentar, completar).
   *
   * Importante: cada chamada compartilha o MESMO `assistantMsgId` (passado
   * pelo caller) pra atualizar visualmente a mesma bubble — evita que cada
   * retry crie uma nova mensagem vazia poluindo o histórico.
   */
  const streamSSEAttempt = useCallback(async (
    message: string,
    effectiveVagaId: string,
    assistantMsgId: string,
    initialContent: string,
  ): Promise<{ ok: true; error?: undefined; content: string } | { ok: false; error: unknown; content: string }> => {
    const assistantContentParts: string[] = initialContent ? [initialContent] : [];
    let hasAssistantContent = initialContent.length > 0;
    let doneEvent = false;
    turnUsageRef.current = { tools: [], freelancers: 0, serverError: false };

    const updateBubble = (status: AssistantMessageStatus, extras: Partial<AssistantMessage> = {}) => {
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== assistantMsgId);
        return [
          ...filtered,
          {
            id: assistantMsgId,
            role: "assistant" as const,
            content: assistantContentParts.join(""),
            timestamp: new Date(),
            status,
            ...extras,
          },
        ];
      });
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Não autenticado");

      // Cria novo AbortController por tentativa — abort cancela tanto o
      // fetch quanto o reader.read().
      abortControllerRef.current = new AbortController();
      const url = `${AGENTS_BASE_URL}/vaga-assistant`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          vaga_id: effectiveVagaId,
          message,
          session_id: sessionId,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new ApiError({
          status: response.status,
          url,
          method: "POST",
          body: body.slice(0, 1000),
        });
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("Stream não disponível");

      const decoder = new TextDecoder();
      let buffer = "";

      // Não criamos a bubble vazia eagerly: com streaming real de tokens, a
      // bubble nasce no primeiro `content` (no lugar certo, depois dos cards),
      // evitando uma bolha vazia "saltando" de posição. O intervalo até o
      // primeiro token é coberto pelo indicador "Pensando..." (AssistantChat)
      // e pelas linhas "Buscando..." de cada tool.

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        let eventType = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ") && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              processSSEEvent(eventType, data);
            } catch {
              // Ignorar linhas malformadas
            }
            eventType = "";
          }
        }
      }

      // Stream encerrou sem `event: done` — provavelmente corte de conexão.
      // Caller decide se vai retentar (mantém o conteúdo parcial).
      if (!doneEvent && hasAssistantContent) {
        return { ok: false, error: new TypeError("stream interrompido sem 'done'"), content: assistantContentParts.join("") };
      }

      if (hasAssistantContent) {
        updateBubble("complete");
      } else {
        // Stream terminou sem conteúdo (agente "calou-se") — remove a bubble
        // vazia que foi criada em `streaming` pra não poluir o histórico.
        setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
      }
      return { ok: true, content: assistantContentParts.join("") };

      function processSSEEvent(type: string, data: Record<string, unknown>) {
        switch (type) {
          case "session":
            if (data.session_id) {
              setSessionId(data.session_id as string);
              sessionIdRef.current = data.session_id as string;
            }
            break;

          case "tool_call": {
            turnUsageRef.current.tools.push(data.name as string);
            const toolMsg: AssistantMessage = {
              id: nextId(),
              role: "tool",
              content: `Buscando: ${data.name}...`,
              toolName: data.name as string,
              toolArgs: data.arguments as Record<string, unknown>,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, toolMsg]);
            break;
          }

          case "tool_result": {
            // Extrair freelancers sugeridos dos resultados
            try {
              const resultStr = data.result as string;
              const resultData = JSON.parse(resultStr);
              if (resultData.freelancers && Array.isArray(resultData.freelancers)) {
                setFreelancersSugeridos((prev) => {
                  const existingIds = new Set(prev.map((f) => f.id));
                  const newFreelancers = resultData.freelancers
                    .filter((f: FreelancerSugerido) => f.id && !existingIds.has(f.id));
                  return [...prev, ...newFreelancers];
                });
              }
              // Extrair sugestões inteligentes
              if (resultData.sugestoes_inteligentes && Array.isArray(resultData.sugestoes_inteligentes)) {
                setSugestoesAtivas(normalizarSugestoes(resultData.sugestoes_inteligentes));
              }
            } catch {
              // Resultado não é JSON ou não tem freelancers
            }
            break;
          }

          case "freelancer_cards": {
            // Cards ricos renderizados inline como uma mensagem própria do
            // assistente (sem texto). Vem só a PÁGINA 1 + metadados; as demais
            // páginas são buscadas sob demanda via serverId (message_id).
            const cards = (data.cards as FreelancerSugerido[]) || [];
            const cardsExploratory = (data.cards_exploratory as FreelancerSugerido[]) || [];
            const transparencia = (data.transparencia as TransparenciaFiltro[]) || [];
            const cardsMeta = (data.cards_meta as CardsMeta) || undefined;
            const serverId = (data.message_id as string) || undefined;
            if (cards.length > 0) {
              turnUsageRef.current.freelancers += cardsMeta?.total_primary ?? cards.length;
              setMessages((prev) => [
                ...prev,
                {
                  id: nextId(),
                  role: "assistant" as const,
                  content: "",
                  cards,
                  cardsExploratory: cardsExploratory.length > 0 ? cardsExploratory : undefined,
                  transparencia,
                  cardsMeta,
                  serverId,
                  status: "complete" as const,
                  timestamp: new Date(),
                },
              ]);
              // Mantém freelancersSugeridos pro lookup do descarte (modal) —
              // inclui as duas listas (a aprendida pode trazer ids exclusivos).
              setFreelancersSugeridos((prev) => {
                const ids = new Set(prev.map((f) => f.id));
                const novos = [...cards, ...cardsExploratory].filter((f) => {
                  if (!f.id || ids.has(f.id)) return false;
                  ids.add(f.id);
                  return true;
                });
                return [...prev, ...novos];
              });
            }
            break;
          }

          case "content":
            if (data.text) {
              hasAssistantContent = true;
              assistantContentParts.push(data.text as string);
              updateBubble("streaming");
            }
            break;

          case "done":
            doneEvent = true;
            break;

          case "error":
            // Erro emitido pelo agente (ex.: tool quebrou). Trata como conteúdo
            // textual da resposta — não é falha de transporte, não retentar.
            turnUsageRef.current.serverError = true;
            assistantContentParts.push(`\n\n⚠️ ${data.message || "Erro desconhecido"}`);
            hasAssistantContent = true;
            updateBubble("streaming");
            break;
        }
      }
    } catch (error) {
      // Falha de transporte / 5xx / corte de conexão: deixa caller decidir
      // se retenta. Mantém conteúdo parcial visível.
      return { ok: false, error, content: assistantContentParts.join("") };
    }
  }, [sessionId]);

  /**
   * Loop de retry sobre `streamSSEAttempt`. Estratégia:
   * 1. Cria a bubble assistente em `streaming` antes do primeiro fetch.
   * 2. Tenta até 3 vezes (300/900/2700ms backoff) se erro for retryable.
   * 3. Entre tentativas, troca status pra `retrying` e mostra contagem.
   * 4. Se conteúdo parcial existir entre tentativas, mantém ele visível
   *    (passa como `initialContent` pra próxima tentativa — mas começa
   *    do zero pq o backend não suporta resume; conteúdo parcial vira
   *    só "histórico" se retry falhar).
   * 5. Esgotou retries → status=`error` com detail do último erro.
   */
  const runWithRetry = useCallback(async (message: string): Promise<EventResult> => {
    const effectiveVagaId = activeVagaId ?? vagaId;
    const assistantMsgId = nextId();

    let lastError: unknown = null;
    let preservedContent = "";

    for (let attempt = 0; attempt < RETRY_DELAYS_MS.length + 1; attempt++) {
      // attempt 0 = primeira tentativa; 1+ = retries
      if (attempt > 0) {
        // Mostrar visualmente que está retentando ANTES de esperar
        const delay = RETRY_DELAYS_MS[attempt - 1];
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== assistantMsgId);
          return [
            ...filtered,
            {
              id: assistantMsgId,
              role: "assistant" as const,
              content: preservedContent,
              timestamp: new Date(),
              status: "retrying",
              retryAttempt: attempt,
              retryMax: RETRY_DELAYS_MS.length,
              retryDelayMs: delay,
              errorDetail: lastError ? formatApiError(lastError).detail : undefined,
            },
          ];
        });
        await new Promise((r) => setTimeout(r, delay));
      }

      // Pra retry, começamos do zero (sem `initialContent`) porque a API
      // do agente não suporta resume — uma nova execução vai gerar resposta
      // do começo. O conteúdo preservado fica só visualmente até ser
      // substituído pelo novo stream.
      const result = await streamSSEAttempt(message, effectiveVagaId, assistantMsgId, "");
      if (result.ok) return turnUsageRef.current.serverError ? "erro" : "ok"; // sucesso: bubble já está em "complete"

      lastError = result.error;
      preservedContent = result.content;

      // Cancelamento manual: não retentar, não mostrar erro.
      if (userCancelledRef.current) {
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== assistantMsgId);
          if (!preservedContent) return filtered; // remove bubble vazia
          return [
            ...filtered,
            {
              id: assistantMsgId,
              role: "assistant" as const,
              content: preservedContent,
              timestamp: new Date(),
              status: "complete",
            },
          ];
        });
        return "cancelado";
      }

      if (!isRetryableError(result.error)) break; // 4xx ou erro não-retryable
      if (attempt >= RETRY_DELAYS_MS.length) break; // esgotou retries
    }

    // Esgotou: mostrar estado de erro com botão retry manual.
    reportApiError(lastError, { source: "vaga_assistant", kind: "send_message" });
    setMessages((prev) => {
      const filtered = prev.filter((m) => m.id !== assistantMsgId);
      return [
        ...filtered,
        {
          id: assistantMsgId,
          role: "assistant" as const,
          content: preservedContent || "",
          timestamp: new Date(),
          status: preservedContent ? "partial" : "error",
          errorDetail: formatApiError(lastError).detail,
        },
      ];
    });
    return "erro";
  }, [vagaId, activeVagaId, streamSSEAttempt]);

  /** One turn in analytics: how long it took, how it ended, the tools it used and how many freelancers it brought. */
  const runTrackedTurn = useCallback(async (message: string, tipo: "nova_conversa" | "continuacao" | "retentativa") => {
    const base = { tipo, contexto_tipo: "vaga", vaga_id: activeVagaId ?? vagaId };
    const startedAt = Date.now();
    trackEvent("assistente.mensagem_enviar", { contexto: { ...base, conversa_id: sessionIdRef.current } });
    const resultado = await runWithRetry(message);
    const { tools, freelancers } = turnUsageRef.current;
    trackEvent("assistente.resposta", {
      resultado,
      duracaoMs: Date.now() - startedAt,
      contexto: { ...base, conversa_id: sessionIdRef.current, ferramentas: [...new Set(tools)], quantidade: freelancers },
    });
  }, [vagaId, activeVagaId, runWithRetry]);

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim() || isLoading) return;

    lastUserMessageRef.current = message;
    userCancelledRef.current = false;

    setMessages((prev) => [...prev, {
      id: nextId(),
      role: "user",
      content: message,
      timestamp: new Date(),
    }]);
    setIsLoading(true);

    try {
      await runTrackedTurn(message, sessionIdRef.current ? "continuacao" : "nova_conversa");
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isLoading, runTrackedTurn]);

  const cancelStream = useCallback(() => {
    if (!abortControllerRef.current) return;
    userCancelledRef.current = true;
    abortControllerRef.current.abort();
  }, []);

  const retryLastMessage = useCallback(async () => {
    const lastMessage = lastUserMessageRef.current;
    if (!lastMessage || isLoading) return;

    userCancelledRef.current = false;

    // Remove a bubble de erro anterior antes de retentar — vai criar nova
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === "assistant" && (last.status === "error" || last.status === "partial")) {
        return prev.slice(0, -1);
      }
      return prev;
    });

    setIsLoading(true);
    try {
      await runTrackedTurn(lastMessage, "retentativa");
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isLoading, runTrackedTurn]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setFreelancersSugeridos([]);
    setSugestoesAtivas([]);
    setActiveVagaId(null);
    setSuggestionsRefresh((prev) => prev + 1);
  }, []);

  return {
    messages,
    isLoading,
    sessionId,
    freelancersSugeridos,
    suggestions,
    sugestoesAtivas,
    loadingSuggestions,
    loadingSession,
    activeVagaId,
    sendMessage,
    retryLastMessage,
    cancelStream,
    clearMessages,
    loadSession,
  };
}
